import { PhxDateTimeFormatPipe, PhxPipesModule } from 'phx-core';

import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { NgModule } from '@angular/core';

import { AGModelNamePipeModule } from '@pipes/ag-model-name-module';
import { AGCheckInRatePipeModule } from '@pipes/agCheckInRate.module';
import { CapitalizePipeModule } from '@pipes/capitalize.module';
import { DistancePipeModule } from '@pipes/distance.module';
import { DurationPipeModule } from '@pipes/duration.module';
import { ElapsedTimeLivePipeModule } from '@pipes/elapsed-time-live.module';
import { MetricUnitPipeModule } from '@pipes/metric-units.module';
import { MomentPipeModule } from '@pipes/moment.module';
import { SpeedPipeModule } from '@pipes/speed.module';
import { TruncateStringPartsPipeModule } from '@pipes/truncate-string-parts.module';
import { EpochDateTimePipeModule } from './epochDateTime.module';

import { AGCheckInRatePipe } from '@pipes/ag-check-in-rate.pipe';
import { AGModelNamePipe } from '@pipes/ag-model-name.pipe';
import { BearingPipe } from '@pipes/bearing';
import { BooleanPipe } from '@pipes/boolean';
import { BulkImportStatusPipe } from '@pipes/bulk-import-status';
import { BytesPipe } from '@pipes/bytes';
import { ContextualTimestampPipe } from '@pipes/contextual-timestamp';
import { CoordinatePipe } from '@pipes/coordinate';
import { CurrencyScale } from '@pipes/currency-scale';
import { CyclePipe } from '@pipes/cycle';
import { NewCycleExceptionPipe } from '@pipes/cycle-exception-new';
import { NewCyclePipe } from '@pipes/cycle-new';
import { CycleExceptionPipe } from '@pipes/cycle-exception';
import { DateRangePipe } from '@pipes/date-range.pipe';
import { DeferredTimePipe } from '@pipes/deferred-time';
import { DistancePipe } from '@pipes/distance';
import { DurationPipe } from '@pipes/duration';
import { DutyStatusDurationPipe } from '@pipes/duty-status-duration';
import { DutyStatusLabelPipe } from '@pipes/duty-status-label';
import { DvirStatusPipe } from '@pipes/dvir-status';
import { ElapsedTimePipe } from '@pipes/elapsed-time';
import { FileSizePipe } from '@pipes/file-size';
import { FindPipe } from '@pipes/find';
import { FloorPipe } from '@pipes/floor';
import { FuelEconomyPipe } from '@pipes/fuel-economy';
import { FuelTypePipe } from '@pipes/fuel-type';
import { FullNamePipe } from '@pipes/full-name';
import { HighlightStringPipe } from '@pipes/highlight-string';
import { HosDurationPipe } from '@pipes/hos-duration';
import { HoursAvailablePipe } from '@pipes/hours-available';
import { HumanizePipe } from '@pipes/humanize';
import { IftaDistancePipe } from '@pipes/ifta/distance';
import { FuelImportStatusPipe } from '@pipes/ifta/fuel-import-status';
import { IftaJurisdictionPipe } from '@pipes/ifta/jurisdiction';
import { IftaOdometerPipe } from '@pipes/ifta/odometer';
import { IntervalPipe } from '@pipes/interval.pipe';
import { MaskCardPipe } from '@pipes/mask-card';
import { MissingTextPipe } from '@pipes/missing-text';
import { MomentPipe } from '@pipes/moment';
import { NumberFromScalePipe } from '@pipes/number-from-scale';
import { OrderedKeyValue } from '@pipes/ordered-key-value';
import { PhonePipe } from '@pipes/phone.pipe';
import { PressurePipe } from '@pipes/pressure';
import { ReplaceALLPipe } from '@pipes/replace-all.pipe';
import { ScaleFromNumber } from '@pipes/scale-from-number';
import { SentenceCasePipe } from '@pipes/sentence-case.pipe';
import { ShortenHosViolationPipe } from '@pipes/shorten-hos-violation';
import { SpeedValuePipe } from '@pipes/speed';
import { SpeedLimitsPipe } from '@pipes/speed-limits.pipe';
import { StatePipe } from '@pipes/states';
import { TemperaturePipe } from '@pipes/temperature';
import { TemperatureDeviationPipe } from './temperatureDeviation.pipe';
import { TimeDifferencePipe } from '@pipes/time-diff';
import { TimestampPipe } from '@pipes/timestamp';
import { TruncatePipe } from '@pipes/truncate';
import { UniquePipe } from '@pipes/unique';
import { UserRolePipe } from '@pipes/user-role';
import { VolumePipe } from '@pipes/volume';
import { WeightUnitPipe } from '@pipes/weight-unit';
import { WifiStatusPipe } from '@pipes/wifi-status';
import { ConditionPipe } from '@pipes/condition.pipe';
import { FormatDataPipe } from '@pipes/format-data.pipe';
import { EpochDateTimeConverter } from './epochDateTime';
import { SecondaryKeyPipe } from '@pipes/secondary-key.pipe';
import { PhoneIntlFormatPipe } from '@pipes/phone-intl-format.pipe';
import { MetricDistanceUnitsPipe } from './metric-distance-units.pipe';

import { ConfigConditionService } from '@reports/report/config-condition.service';
import { ConfigFiltersService } from '@reports/report/config-filters.service';
import { TableUtilsService } from '@reports/report/table/table-utils.service';
import { CheckboxIconColor } from './checkbox-icon-color.pipe';
import { CheckboxIcon } from './checkbox-icon.pipe';
import { HosViolationPipe } from './hos-violation.pipe';
import { EvEconomyPipe } from './ev-economy.pipe';
import { CoerceBooleanPipe } from './coerce-boolean.pipe';

const COMMON_PIPES = [
  BooleanPipe,
  CoordinatePipe,
  CyclePipe,
  NewCyclePipe,
  NewCycleExceptionPipe,
  CycleExceptionPipe,
  FloorPipe,
  WeightUnitPipe,
  FileSizePipe,
  BytesPipe,
  DutyStatusDurationPipe,
  DvirStatusPipe,
  FuelTypePipe,
  HoursAvailablePipe,
  HumanizePipe,
  MissingTextPipe,
  TimestampPipe,
  UniquePipe,
  VolumePipe,
  ElapsedTimePipe,
  TruncatePipe,
  IftaOdometerPipe,
  IftaDistancePipe,
  IftaJurisdictionPipe,
  FuelImportStatusPipe,
  BearingPipe,
  UserRolePipe,
  HosDurationPipe,
  NumberFromScalePipe,
  TemperaturePipe,
  TemperatureDeviationPipe,
  PressurePipe,
  DutyStatusLabelPipe,
  TimeDifferencePipe,
  ScaleFromNumber,
  SpeedLimitsPipe,
  PhonePipe,
  HosViolationPipe,
  DeferredTimePipe,
  FuelEconomyPipe,
  CurrencyScale,
  ShortenHosViolationPipe,
  StatePipe,
  FindPipe,
  HighlightStringPipe,
  ContextualTimestampPipe,
  ReplaceALLPipe,
  MaskCardPipe,
  OrderedKeyValue,
  FormatDataPipe,
  ConditionPipe,
  BulkImportStatusPipe,
  DateRangePipe,
  FullNamePipe,
  IntervalPipe,
  SentenceCasePipe,
  WifiStatusPipe,
  SecondaryKeyPipe,
  CheckboxIconColor,
  CheckboxIcon,
  EvEconomyPipe,
  PhoneIntlFormatPipe,
  CoerceBooleanPipe,
];

@NgModule({
  declarations: COMMON_PIPES,
  imports: [PhxPipesModule],
  exports: [
    ...COMMON_PIPES,
    ElapsedTimeLivePipeModule,
    MetricUnitPipeModule,
    SpeedPipeModule,
    EpochDateTimePipeModule,
    DistancePipeModule,
    MomentPipeModule,
    DurationPipeModule,
    AGCheckInRatePipeModule,
    TruncateStringPartsPipeModule,
    CapitalizePipeModule,
    AGModelNamePipeModule,
  ],
  providers: [
    DecimalPipe,
    PercentPipe,
    DatePipe,
    SpeedValuePipe,
    EpochDateTimeConverter,
    TemperaturePipe,
    TemperatureDeviationPipe,
    PressurePipe,
    DistancePipe,
    MomentPipe,
    DurationPipe,
    CurrencyPipe,
    ScaleFromNumber,
    HumanizePipe,
    DutyStatusDurationPipe,
    DvirStatusPipe,
    StatePipe,
    FindPipe,
    IftaDistancePipe,
    IftaJurisdictionPipe,
    IftaOdometerPipe,
    FuelTypePipe,
    VolumePipe,
    CoordinatePipe,
    WeightUnitPipe,
    FullNamePipe,
    DateRangePipe,
    TableUtilsService,
    ConfigConditionService,
    DutyStatusDurationPipe,
    ConfigFiltersService,
    ElapsedTimeLivePipeModule,
    MetricUnitPipeModule,
    SpeedPipeModule,
    DurationPipeModule,
    TruncateStringPartsPipeModule,
    CapitalizePipeModule,
    AGModelNamePipeModule,
    AGModelNamePipe,
    SentenceCasePipe,
    IntervalPipe,
    WifiStatusPipe,
    AGCheckInRatePipe,
    AGCheckInRatePipeModule,
    HosDurationPipe,
    CheckboxIconColor,
    CheckboxIcon,
    FileSizePipe,
    PhxDateTimeFormatPipe,
    FuelEconomyPipe,
    EvEconomyPipe,
    SecondaryKeyPipe,
    MetricDistanceUnitsPipe,
    CoerceBooleanPipe,
  ],
})
export class CommonPipesModule {}
